import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { CreatePettyCashEntrySchema, CreatePettyCashFloatSchema, fmt, fmtB } from '@dcc-erp/shared';
import type {
  CreatePettyCashEntry,
  CreatePettyCashFloat,
  PettyCashClose,
  PettyCashCount,
  PettyCashFloatListRow,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  monthRange,
  rollupByFloat,
  summarizeByCategory,
  type FloatRollup,
} from './pettycash.logic';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { EmptyTabCard } from '../components/pettycash/EmptyTabCard';
import { PCCategory } from '../components/pettycash/PCCategory';
import { PCCloseApprovalPanel } from '../components/pettycash/PCCloseApprovalPanel';
import { PCCloseModal } from '../components/pettycash/PCCloseModal';
import { PCCountApprovalPanel } from '../components/pettycash/PCCountApprovalPanel';
import { PCCountModal } from '../components/pettycash/PCCountModal';
import { PCEntries } from '../components/pettycash/PCEntries';
import { PCEntryApprovalPanel } from '../components/pettycash/PCEntryApprovalPanel';
import { PCEntryModal } from '../components/pettycash/PCEntryModal';
import { PCOverview } from '../components/pettycash/PCOverview';
import { PCReplen } from '../components/pettycash/PCReplen';
import { PCReplenModal } from '../components/pettycash/PCReplenModal';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import { useBranches } from '../lib/queries/branches';
import { useCoa } from '../lib/queries/coa';
import { useActiveCompany, useActiveCompanyId } from '../lib/queries/companies';
import { useEmployees } from '../lib/queries/employees';
import {
  useCreatePettyCashEntry,
  useCreatePettyCashFloat,
  usePettyCashCloses,
  usePettyCashCounts,
  usePettyCashEntries,
  usePettyCashFloats,
  usePettyCashReplenishments,
} from '../lib/queries/petty-cash';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

import type { Branch, BranchStatus, EntryWithApproval } from '../components/pettycash/pc-helpers';

type OverviewTab = 'overview' | 'entries' | 'replen' | 'category';

interface Aggregate {
  total: number;
  entries: number;
  float: number;
  used: number;
  pending: number;
  submitted: number;
  approved: number;
  reimbursed: number;
  overdue: number;
}

// Abbreviated Thai month names, indexed 0..11.
const TH_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
] as const;

// 'YYYY-MM' → Thai month-year label (Buddhist era), e.g. '2026-05' → 'พ.ค. 2569'.
// Matches the period <select> option labels so the page title tracks the
// selected period exactly.
function periodLabel(yyyymm: string): string {
  const parts = yyyymm.split('-');
  const year = Number(parts[0]);
  const monthIdx = Number(parts[1]) - 1;
  return `${TH_MONTHS[monthIdx] ?? ''} ${year + 543}`;
}

// นำเข้า: one spreadsheet row = open a petty-cash float for a branch.
const PC_FLOAT_IMPORT_FIELDS: ImportFieldSpec[] = [
  { col: 'branchCode', label: 'รหัสสาขา', required: true, hint: 'รหัสสาขาที่จะเปิดวงเงิน' },
  {
    col: 'floatAmount',
    label: 'วงเงิน (Float)',
    required: true,
    hint: 'จำนวนเงินวงเงิน ต้องมากกว่า 0',
  },
  {
    col: 'openedDate',
    label: 'วันที่เปิด',
    required: false,
    hint: 'YYYY-MM-DD (ไม่ระบุ = วันนี้)',
  },
  {
    col: 'custodianCode',
    label: 'รหัสพนักงานผู้รับผิดชอบ',
    required: false,
    hint: 'รหัสพนักงาน เช่น EMP-0042 (ไม่ระบุได้)',
  },
];

const PC_FLOAT_TEMPLATE_COLUMNS = ['branchCode', 'floatAmount', 'openedDate', 'custodianCode'];
const PC_FLOAT_TEMPLATE_EXAMPLES = [['BR001', 20000, '2026-05-01', 'EMP-0042']];

// นำเข้า: one spreadsheet row = back-enter one petty-cash ENTRY against a
// branch's active float. Bulk paper-receipt back-entry — each row carries
// isNoDoc:true so it satisfies the server's receipt hard-block without a
// digital attachment (the paper voucher is the supporting document).
const PC_ENTRY_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: true,
    hint: 'รหัสสาขาที่มี float active อยู่',
  },
  {
    col: 'entryDate',
    label: 'วันที่ทำรายการ',
    required: false,
    hint: 'YYYY-MM-DD (ไม่ระบุ = วันนี้)',
  },
  {
    col: 'categoryCode',
    label: 'รหัสบัญชีค่าใช้จ่าย',
    required: true,
    hint: 'รหัสผังบัญชีค่าใช้จ่าย/ต้นทุน เช่น 60110003',
  },
  { col: 'description', label: 'รายละเอียด', required: true, hint: 'คำอธิบายรายการ' },
  { col: 'amount', label: 'จำนวนเงิน', required: true, hint: 'จำนวนเงิน ต้องมากกว่า 0' },
  {
    col: 'payeeName',
    label: 'ผู้รับเงิน / ร้านค้า',
    required: false,
    hint: 'ชื่อผู้รับเงินหรือร้านค้า (ไม่บังคับ)',
  },
];

const PC_ENTRY_TEMPLATE_COLUMNS = [
  'branchCode',
  'entryDate',
  'categoryCode',
  'description',
  'amount',
  'payeeName',
];
const PC_ENTRY_TEMPLATE_EXAMPLES = [
  ['BR001', '2026-05-10', '60110003', 'เติมน้ำมัน 95 รถ Hilux', 850, 'ปั๊ม ปตท. บางนา'],
];

// Adapter — PettyCashFloatListRow (API) → Branch (prototype DOM). The "200
// branches × 10 categories × N entries" mock generator was prototype-only;
// real data comes from /pc/floats. Per-branch entry counts / total spend come
// from a client-side rollup of the live entries query (rollupByFloat), keyed
// by float id; the float's own currentBalance still drives used/remain.
//
// Two distinct money concepts, deliberately NOT conflated:
//   • total/entries — PERIOD spend: sum/count of entries in the selected month
//     (0 when the float has no entries this period — its lifetime drawdown is
//     irrelevant here). Drives "ยอดเบิกจ่ายรวม" KPI + category-% denominator.
//   • used/remain/usedPct — float BALANCE: lifetime drawdown vs currentBalance.
//     Drives the "ใช้ไป" figure + progress bar.
function mapFloatToBranch(f: PettyCashFloatListRow, rollup?: FloatRollup): Branch {
  const floatAmt = Number(f.floatAmount);
  const currentBal = Number(f.currentBalance);
  const used = Math.max(0, floatAmt - currentBal);
  const usedPct = floatAmt > 0 ? Math.round((used / floatAmt) * 100) : 0;
  let status: BranchStatus = 'submitted';
  if (f.status === 'CLOSED') status = 'reimbursed';
  return {
    id: f.branchCode ?? f.id.slice(0, 8),
    floatId: f.id,
    branchUuid: f.branchId,
    name: f.branchName ?? '—',
    entries: rollup?.count ?? 0,
    total: rollup?.total ?? 0,
    float: floatAmt,
    used,
    remain: currentBal,
    usedPct,
    status,
    lastSub: rollup?.lastEntryDate ?? new Date(f.updatedAt).toISOString().slice(0, 10),
    custodian: f.custodianName ?? '—',
  };
}

export const Route = createFileRoute('/pettycash')({
  component: PettyCashPage,
});

export function PettyCashPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const [tab, setTab] = useState<OverviewTab>('overview');
  const [selBranch, setSelBranch] = useState<Branch | null>(null);
  const [q, setQ] = useState<string>('');
  const [statusF, setStatusF] = useState<string>('all');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [showEntry, setShowEntry] = useState<boolean>(false);
  const [showReplen, setShowReplen] = useState<boolean>(false);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [showEntryImport, setShowEntryImport] = useState<boolean>(false);
  const [replenFloatId, setReplenFloatId] = useState<string | undefined>(undefined);
  // M3 maker-checker — the entry whose central-approval panel is open.
  const [approvalEntry, setApprovalEntry] = useState<EntryWithApproval | null>(null);
  // M4 — cash-count (PCC) + float-close (PCX) modals + their approval panels.
  const [countFloatId, setCountFloatId] = useState<string | undefined>(undefined);
  const [closeFloatId, setCloseFloatId] = useState<string | undefined>(undefined);
  const [approvalCount, setApprovalCount] = useState<PettyCashCount | null>(null);
  const [approvalClose, setApprovalClose] = useState<PettyCashClose | null>(null);

  // Period drives the all-entries query date window. Defaults to the current
  // calendar month; the period <select> in the page header is wired to setPeriod
  // (Task 9 will flesh out the option list / cross-month UX).
  const [period, setPeriod] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const { dateFrom, dateTo } = useMemo(() => monthRange(period), [period]);

  // Live floats from API — the only branch source now. Mock generator removed.
  // Floats carry only a branch dimension (no department column), so the list
  // filter here is branch-only.
  const floatsQuery = usePettyCashFloats(
    companyId,
    filterBranchId ? { branchId: filterBranchId } : {},
  );
  const liveFloats = useMemo(() => floatsQuery.data ?? [], [floatsQuery.data]);

  // All petty-cash entries in the period (company-wide) — filtered to the
  // selected float in the entries tab, and rolled up per float for the
  // overview/KPI counts. COA gives category-code → Thai name lookups.
  const entriesQuery = usePettyCashEntries(companyId, { dateFrom, dateTo });
  const liveEntries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);
  const floatRollups = useMemo(() => rollupByFloat(liveEntries), [liveEntries]);
  const { data: coaList = [] } = useCoa(companyId, true);

  // Company-wide replenishments — drives the replen tab queue and history.
  const replenQuery = usePettyCashReplenishments(companyId);
  const liveReplen = useMemo(() => replenQuery.data ?? [], [replenQuery.data]);

  // M4 — company-wide cash counts (PCC) + float closes (PCX); the entries view
  // narrows them to the selected float.
  const countsQuery = usePettyCashCounts(companyId);
  const liveCounts = useMemo(() => countsQuery.data ?? [], [countsQuery.data]);
  const closesQuery = usePettyCashCloses(companyId);
  const liveCloses = useMemo(() => closesQuery.data ?? [], [closesQuery.data]);

  // Masters + create hook for the นำเข้า / ส่งออก flow. Export uses the RAW
  // float list (not the mapped Branch prototypes), so a file round-trips back
  // through นำเข้า cleanly.
  const { data: branchMasters = [] } = useBranches(companyId);
  const { data: employees = [] } = useEmployees(companyId);
  const createFloat = useCreatePettyCashFloat(companyId);
  const createEntry = useCreatePettyCashEntry(companyId);
  const branchByCode = useMemo(
    () => new Map(branchMasters.map((b) => [b.code.toLowerCase(), b.id])),
    [branchMasters],
  );
  const branchLabelById = useMemo(
    () => new Map(branchMasters.map((b) => [b.id, `${b.code} — ${b.nameTh}`])),
    [branchMasters],
  );
  // Employee code → id map for custodian resolution in the float import.
  const employeeByCode = useMemo(
    () => new Map(employees.map((e) => [e.code.toLowerCase(), e.id])),
    [employees],
  );
  const employeeLabelById = useMemo(
    () => new Map(employees.map((e) => [e.id, `${e.code} — ${e.nameTh}`])),
    [employees],
  );
  // float id → "branchName" label, for the entry-import preview table.
  const floatLabelById = useMemo(
    () => new Map(liveFloats.map((f) => [f.id, f.branchName ?? f.branchCode ?? f.id])),
    [liveFloats],
  );
  // COA code → Thai name, for the entry-import preview category cell.
  const coaNameByCode = useMemo(() => new Map(coaList.map((a) => [a.code, a.nameTh])), [coaList]);

  const FLOAT_EXPORT_COLS: ExportColumn<PettyCashFloatListRow>[] = useMemo(
    () => [
      { header: 'branchCode', value: (r) => r.branchCode ?? '', width: 12 },
      { header: 'floatAmount', value: (r) => Number(r.floatAmount), width: 14 },
      { header: 'openedDate', value: (r) => String(r.openedDate).slice(0, 10), width: 12 },
      // read-only / computed columns (ignored on re-import)
      { header: 'branchName', value: (r) => r.branchName, width: 24 },
      { header: 'currentBalance', value: (r) => Number(r.currentBalance), width: 14 },
      { header: 'status', value: (r) => r.status, width: 10 },
      { header: 'postedBy', value: (r) => r.postedBy ?? '', width: 18 },
    ],
    [],
  );

  async function handleExport() {
    await exportRows(
      'เงินสดย่อย_' + (company?.code ?? 'COMPANY') + '_' + todayStamp() + '.xlsx',
      'เงินสดย่อย',
      FLOAT_EXPORT_COLS,
      liveFloats,
    );
  }

  function parseFloats(grid: string[][]): {
    units: ParsedUnit<CreatePettyCashFloat>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of ['branchCode', 'floatAmount']) {
      if (idx(req) === -1) return { units: [], headerError: 'ไม่พบคอลัมน์ที่จำเป็น: ' + req };
    }
    const units: ParsedUnit<CreatePettyCashFloat>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (!branchId) {
        units.push({ rowNo, data: null, error: 'ไม่พบสาขา: ' + branchCode });
        continue;
      }
      // Optional custodian: resolve code → id; error only if code given but not found.
      const custodianCode = idx('custodianCode') !== -1 ? cell(cols, 'custodianCode') : '';
      const custodianId = custodianCode
        ? employeeByCode.get(custodianCode.toLowerCase())
        : undefined;
      if (custodianCode && !custodianId) {
        units.push({ rowNo, data: null, error: `ไม่พบพนักงาน: ${custodianCode}` });
        continue;
      }
      const openedDate = cell(cols, 'openedDate') || new Date().toISOString().slice(0, 10);
      const candidate = {
        branchId,
        floatAmount: Number(cell(cols, 'floatAmount')),
        openedDate,
        ...(custodianId ? { custodianId } : {}),
      };
      const p = CreatePettyCashFloatSchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreatePettyCashFloat>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createFloat.mutateAsync({ data: u.data, idempotencyKey: crypto.randomUUID() });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: 'แถว ' + u.rowNo,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const FLOAT_PREVIEW_COLS: PreviewColumn<CreatePettyCashFloat>[] = [
    { header: 'สาขา', width: 18, render: (d) => branchLabelById.get(d.branchId) ?? d.branchId },
    { header: 'วงเงิน', width: 14, render: (d) => fmt(d.floatAmount) },
    { header: 'วันที่เปิด', width: 12, render: (d) => String(d.openedDate).slice(0, 10) },
    {
      header: 'ผู้รับผิดชอบ',
      width: 20,
      render: (d) =>
        d.custodianId ? (employeeLabelById.get(d.custodianId) ?? d.custodianId) : '—',
    },
  ];

  // ─── ENTRY import (mirror of the float import) ─────────────────────────────
  // branchCode → the branch's ACTIVE float id. A branch can only carry one
  // active float, so this is a 1:1 map; rows for branches with no active float
  // are rejected (you must open a float before back-entering its receipts).
  const floatIdByBranchCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of liveFloats) {
      if (f.status === 'ACTIVE' && f.branchCode) m.set(f.branchCode.toLowerCase(), f.id);
    }
    return m;
  }, [liveFloats]);
  // Postable expense/cost COA codes — the same set the entry modal offers and
  // the server validates against. Used to reject unknown categoryCode rows.
  const postableCategoryCodes = useMemo(
    () =>
      new Set(
        coaList
          .filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl)
          .map((a) => a.code),
      ),
    [coaList],
  );

  function parseEntries(grid: string[][]): {
    units: ParsedUnit<CreatePettyCashEntry>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of ['branchCode', 'categoryCode', 'description', 'amount']) {
      if (idx(req) === -1) return { units: [], headerError: 'ไม่พบคอลัมน์ที่จำเป็น: ' + req };
    }
    const units: ParsedUnit<CreatePettyCashEntry>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;
      const branchCode = cell(cols, 'branchCode');
      const floatId = branchCode ? floatIdByBranchCode.get(branchCode.toLowerCase()) : undefined;
      if (!floatId) {
        units.push({
          rowNo,
          data: null,
          error: 'ไม่พบ float ที่ active สำหรับสาขา: ' + branchCode,
        });
        continue;
      }
      const categoryCode = cell(cols, 'categoryCode');
      if (!postableCategoryCodes.has(categoryCode)) {
        units.push({
          rowNo,
          data: null,
          error: 'ไม่พบบัญชีค่าใช้จ่ายที่ใช้ได้: ' + categoryCode,
        });
        continue;
      }
      const entryDate =
        (idx('entryDate') !== -1 ? cell(cols, 'entryDate') : '') ||
        new Date().toISOString().slice(0, 10);
      const payeeName = idx('payeeName') !== -1 ? cell(cols, 'payeeName').trim() : '';
      const candidate = {
        floatId,
        entryDate,
        description: cell(cols, 'description'),
        categoryCode,
        amount: Number(cell(cols, 'amount')),
        ...(payeeName ? { payeeName } : {}),
        // Bulk paper-receipt back-entry satisfies the server's receipt
        // hard-block without a digital attachment.
        isNoDoc: true,
      };
      const p = CreatePettyCashEntrySchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runEntryImport(
    valid: ParsedUnit<CreatePettyCashEntry>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createEntry.mutateAsync({ data: u.data, idempotencyKey: crypto.randomUUID() });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: 'แถว ' + u.rowNo,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const ENTRY_PREVIEW_COLS: PreviewColumn<CreatePettyCashEntry>[] = [
    {
      header: 'สาขา (Float)',
      width: 18,
      render: (d) => floatLabelById.get(d.floatId) ?? d.floatId,
    },
    { header: 'วันที่', width: 12, render: (d) => String(d.entryDate).slice(0, 10) },
    {
      header: 'ประเภท',
      width: 18,
      render: (d) => `${d.categoryCode} — ${coaNameByCode.get(d.categoryCode) ?? '—'}`,
    },
    { header: 'รายละเอียด', width: 24, render: (d) => d.description },
    { header: 'จำนวนเงิน', width: 14, render: (d) => fmt(d.amount) },
    { header: 'ผู้รับเงิน', width: 16, render: (d) => d.payeeName ?? '—' },
  ];

  const allBranches = useMemo<readonly Branch[]>(
    () => liveFloats.map((f) => mapFloatToBranch(f, floatRollups.get(f.id))),
    [liveFloats, floatRollups],
  );

  const branches = useMemo<readonly Branch[]>(
    () =>
      allBranches.filter((b) => {
        if (filterBranchId && b.id !== filterBranchId) return false;
        if (statusF !== 'all' && b.status !== statusF) return false;
        if (
          q &&
          !(
            b.id.toLowerCase().includes(q.toLowerCase()) ||
            b.name.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [allBranches, filterBranchId, statusF, q],
  );

  const agg = useMemo<Aggregate>(
    () =>
      allBranches.reduce<Aggregate>(
        (a, b) => ({
          total: a.total + b.total,
          entries: a.entries + b.entries,
          float: a.float + b.float,
          used: a.used + b.used,
          pending: a.pending + (b.status === 'pending' ? 1 : 0),
          submitted: a.submitted + (b.status === 'submitted' ? 1 : 0),
          approved: a.approved + (b.status === 'approved' ? 1 : 0),
          reimbursed: a.reimbursed + (b.status === 'reimbursed' ? 1 : 0),
          overdue: a.overdue + (b.status === 'overdue' ? 1 : 0),
        }),
        {
          total: 0,
          entries: 0,
          float: 0,
          used: 0,
          pending: 0,
          submitted: 0,
          approved: 0,
          reimbursed: 0,
          overdue: 0,
        },
      ),
    [allBranches],
  );

  // Category breakdown: client-side rollup of live entries by categoryCode.
  const categoryRows = useMemo(() => summarizeByCategory(liveEntries), [liveEntries]);
  // Top-10 branches by total spend (branches with zero spend are excluded).
  const topBranches = useMemo(
    () =>
      [...allBranches]
        .filter((b) => b.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10),
    [allBranches],
  );

  const tabs: readonly (readonly [OverviewTab, string])[] = [
    ['overview', 'สถานะรายสาขา'],
    ['entries', 'สมุดเงินสดย่อยรายสาขา'],
    ['replen', 'ใบเบิกชดเชย (Replenishment)'],
    ['category', 'สรุปตามประเภทค่าใช้จ่าย'],
  ];

  // Rolling 6-month period options (current month + 5 prior), as 'YYYY-MM'.
  // Wires the header <select> to setPeriod so the entries window is live.
  const periodOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { value, label: `งวด: ${periodLabel(value)}` };
    });
  }, []);

  // Page title + downstream labels track the SELECTED period (not a hardcoded
  // month) so the header, summary card, and daily chart all agree.
  const selectedPeriodLabel = useMemo(() => periodLabel(period), [period]);

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'เงินสดย่อย']}
        title={`เงินสดย่อย — รายงานรวมรายสาขา · เดือน ${selectedPeriodLabel}`}
        actions={
          <>
            <HelpButton guide="pettycash" />
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {periodOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={liveFloats.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowEntry(true)}>
              <Icon name="plus" size={14} /> บันทึกรายการใหม่
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ยอดเบิกจ่ายรวม
          </div>
          <div className="value tnum">
            {fmtB(agg.total)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {fmt(agg.entries)} รายการ ·{' '}
            {agg.entries > 0
              ? `ค่าเฉลี่ย ${fmt(Math.round(agg.total / agg.entries))} บาท/ใบ`
              : 'ยังไม่มีรายการ'}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            วงเงินรวม (Float)
          </div>
          <div className="value tnum">
            {fmtB(agg.float)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {agg.float > 0
              ? `ใช้ไป ${Math.round((agg.used / agg.float) * 100)}% · คงเหลือ ${fmtB(
                  agg.float - agg.used,
                )} บาท`
              : 'ยังไม่มี float ที่เปิด'}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            สาขาทั้งหมด
          </div>
          <div className="value tnum">
            {fmt(allBranches.length)} <span className="unit">สาขา</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> {agg.submitted + agg.approved + agg.reimbursed}{' '}
            สาขาส่งแล้ว
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            รออนุมัติ / เกินกำหนด
          </div>
          <div className="value tnum">
            {fmt(agg.pending + agg.overdue)} <span className="unit">สาขา</span>
          </div>
          <div className="trend down">{agg.overdue} สาขาเกินกำหนดส่ง</div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'overview' && (
        <PCOverview
          branches={branches}
          companyId={companyId}
          filterBranchId={filterBranchId}
          setFilterBranchId={setFilterBranchId}
          statusF={statusF}
          setStatusF={setStatusF}
          q={q}
          setQ={setQ}
          isPending={floatsQuery.isPending || entriesQuery.isPending}
          error={floatsQuery.error}
          onPick={(b) => {
            setSelBranch(b);
            setTab('entries');
          }}
        />
      )}
      {tab === 'entries' && selBranch ? (
        <PCEntries
          branch={selBranch}
          allBranches={allBranches}
          setBranch={setSelBranch}
          entries={liveEntries.filter((e) => e.floatId === selBranch.floatId)}
          coa={coaList}
          companyName={company?.code}
          period={period}
          periodLabel={selectedPeriodLabel}
          isPending={entriesQuery.isPending}
          onAdd={() => setShowEntry(true)}
          onImport={() => setShowEntryImport(true)}
          onOpenApproval={(e) => setApprovalEntry(e)}
          onCount={() => setCountFloatId(selBranch.floatId)}
          onClose={() => setCloseFloatId(selBranch.floatId)}
          counts={liveCounts.filter((c) => c.floatId === selBranch.floatId)}
          closes={liveCloses.filter((c) => c.floatId === selBranch.floatId)}
          onOpenCountApproval={(c) => setApprovalCount(c)}
          onOpenCloseApproval={(c) => setApprovalClose(c)}
          companyId={companyId}
        />
      ) : tab === 'entries' ? (
        <EmptyTabCard text="กรุณาเลือกสาขาจากแท็บ 'สถานะรายสาขา' ก่อน" />
      ) : null}
      {tab === 'replen' && (
        <PCReplen
          branches={allBranches}
          replenishments={liveReplen}
          onReplenish={(floatId) => {
            setReplenFloatId(floatId);
            setShowReplen(true);
          }}
        />
      )}
      {tab === 'category' && (
        <PCCategory
          rows={categoryRows}
          topBranches={topBranches}
          coa={coaList}
          total={agg.total}
          isPending={entriesQuery.isPending}
        />
      )}

      {showEntry && (
        <PCEntryModal
          branch={selBranch}
          companyId={companyId}
          liveFloats={liveFloats}
          onClose={() => setShowEntry(false)}
        />
      )}
      {showReplen && (
        <PCReplenModal
          companyId={companyId}
          liveFloats={liveFloats}
          initialFloatId={replenFloatId}
          onClose={() => {
            setShowReplen(false);
            setReplenFloatId(undefined);
          }}
        />
      )}
      {showImport && companyId && (
        <SpreadsheetImportModal<CreatePettyCashFloat>
          title="นำเข้าวงเงินเงินสดย่อย"
          intro="หนึ่งแถว = เปิดวงเงินเงินสดย่อย (Float) ให้หนึ่งสาขา"
          fields={PC_FLOAT_IMPORT_FIELDS}
          templateColumns={PC_FLOAT_TEMPLATE_COLUMNS}
          templateExamples={PC_FLOAT_TEMPLATE_EXAMPLES}
          templateFilename="petty_cash_float_template.xlsx"
          sheetName="เงินสดย่อย"
          parse={parseFloats}
          previewColumns={FLOAT_PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {showEntryImport && companyId && (
        <SpreadsheetImportModal<CreatePettyCashEntry>
          title="นำเข้ารายการเงินสดย่อย"
          intro="หนึ่งแถว = บันทึกรายการเงินสดย่อยหนึ่งใบเข้า float ที่ active ของสาขา (ตั้งเป็น 'ไม่แนบใบเสร็จ' อัตโนมัติ — ใช้สำหรับบันทึกย้อนหลังจากเอกสารกระดาษ)"
          fields={PC_ENTRY_IMPORT_FIELDS}
          templateColumns={PC_ENTRY_TEMPLATE_COLUMNS}
          templateExamples={PC_ENTRY_TEMPLATE_EXAMPLES}
          templateFilename="petty_cash_entry_template.xlsx"
          sheetName="รายการเงินสดย่อย"
          parse={parseEntries}
          previewColumns={ENTRY_PREVIEW_COLS}
          runImport={runEntryImport}
          onClose={() => setShowEntryImport(false)}
        />
      )}
      {approvalEntry && (
        <PCEntryApprovalPanel
          companyId={companyId}
          entry={approvalEntry}
          coaName={coaNameByCode}
          onClose={() => setApprovalEntry(null)}
        />
      )}
      {countFloatId && (
        <PCCountModal
          companyId={companyId}
          liveFloats={liveFloats}
          initialFloatId={countFloatId}
          onClose={() => setCountFloatId(undefined)}
        />
      )}
      {closeFloatId && (
        <PCCloseModal
          companyId={companyId}
          liveFloats={liveFloats}
          initialFloatId={closeFloatId}
          onClose={() => setCloseFloatId(undefined)}
        />
      )}
      {approvalCount && (
        <PCCountApprovalPanel
          companyId={companyId}
          count={approvalCount}
          onClose={() => setApprovalCount(null)}
        />
      )}
      {approvalClose && (
        <PCCloseApprovalPanel
          companyId={companyId}
          close={approvalClose}
          onClose={() => setApprovalClose(null)}
        />
      )}
      {/* Floating replen trigger — discoverable from any tab. Visible only when
          live floats exist (i.e. once at least one branch float has been opened). */}
      {!showEntry && !showReplen && liveFloats.length > 0 && (
        <button
          className="btn"
          onClick={() => setShowReplen(true)}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 50,
            boxShadow: '0 12px 30px rgba(0,0,0,0.14)',
          }}
        >
          <Icon name="upload" size={14} /> เติมเงิน (Replenish)
        </button>
      )}
    </div>
  );
}
